/**
 * Reusable "what will the recipient actually see?" preview modal.
 *
 * Used by every send-email composer to give the admin a final sanity check
 * before clicking Send. Renders the body inside a sandboxed iframe so
 * malicious template HTML can't escape the modal.
 *
 * Two ways to provide content:
 *   - Pass `bodyHtml` + `subject` directly (client-side rendered preview)
 *   - Pass `fetchPreview()` async fn returning `{ subject, body_html, to }`
 *     so backend can resolve `{{setup_url}}` etc. — used by `SendEmailDialog`.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Eye, Send, Loader2 } from 'lucide-react';

export const EmailPreviewDialog = ({
  open,
  onOpenChange,
  to,
  cc,
  from,
  subject,
  bodyHtml,
  fetchPreview, // optional: async () => { subject, body_html, to }
  onConfirmSend, // optional: () => void
  sending = false,
  recipientLabel = 'the customer',
}) => {
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState({ subject, bodyHtml, to });

  // When opened, either trust the passed-in props or call the async resolver.
  // We re-run when subject/bodyHtml change too so the modal doesn't go stale
  // if the parent edits the body while the preview is already open.
  useEffect(() => {
    if (!open) return;
    if (!fetchPreview) {
      setResolved({ subject, bodyHtml, to });
      return;
    }
    setLoading(true);
    fetchPreview()
      .then((r) =>
        setResolved({
          subject: r?.subject ?? subject,
          bodyHtml: r?.body_html ?? bodyHtml,
          to: r?.to ?? to,
        }),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject, bodyHtml, to]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        aria-describedby="email-preview-desc"
        data-testid="email-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <Eye className="w-5 h-5" />
            Email Preview — what {recipientLabel} will see
          </DialogTitle>
        </DialogHeader>
        <p id="email-preview-desc" className="sr-only">
          Rendered preview of the email with all template variables resolved.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-3">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1.5 text-sm border border-slate-200 dark:border-slate-700">
              {resolved.to && (
                <div>
                  <span className="font-medium text-slate-500">To:</span>{' '}
                  <span data-testid="preview-to">{resolved.to}</span>
                </div>
              )}
              {cc && (
                <div>
                  <span className="font-medium text-slate-500">CC:</span> {cc}
                </div>
              )}
              {from && (
                <div>
                  <span className="font-medium text-slate-500">From:</span> {from}
                </div>
              )}
              <div>
                <span className="font-medium text-slate-500">Subject:</span>{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100" data-testid="preview-subject">
                  {resolved.subject || <em className="text-slate-400 font-normal">(no subject)</em>}
                </span>
              </div>
            </div>

            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                Email body
              </div>
              <iframe
                title="Email body preview"
                srcDoc={
                  resolved.bodyHtml ||
                  '<p style="color:#94a3b8;font-family:sans-serif;padding:16px">(empty body)</p>'
                }
                sandbox=""
                className="w-full bg-white"
                style={{ height: '50vh', border: 0 }}
                data-testid="preview-iframe"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="close-preview-btn"
          >
            Close
          </Button>
          {onConfirmSend && (
            <Button
              onClick={() => {
                onOpenChange(false);
                onConfirmSend();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={sending}
              data-testid="preview-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Looks good — Send
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewDialog;
